// Desktop ambient mode. Spec section 13.
//
// The same app, opened as a window on a Mac and left running all day. Not a
// separate product, but not a free ride either. Five things genuinely
// differ, and this hook covers the two that are behavioural.
import { useEffect, useRef, useState } from 'react';

export type Platform = 'mobile' | 'desktop';

/**
 * Coarse but sufficient. The consequence of guessing wrong is which origin
 * strategy runs, and both are overridable, so this does not need to be
 * clever.
 */
export function detectPlatform(): Platform {
  if (typeof window === 'undefined') return 'mobile';
  const touch = navigator.maxTouchPoints > 1;
  const narrow = window.matchMedia('(max-width: 700px)').matches;
  return touch && narrow ? 'mobile' : 'desktop';
}

/**
 * Spec 13.3: a window left open all day is not a page load.
 *
 * Do not rely on setInterval alone. Browsers throttle timers in background
 * tabs aggressively, often to once a minute or worse, and macOS throttles
 * further on battery. An interval-driven refresh silently stops being live
 * exactly when you stop looking at it.
 */
export function useAmbientRefresh(refresh: () => void, lastFetchAt: number | null) {
  const lastInteraction = useRef(Date.now());
  const [dormant, setDormant] = useState(false);

  useEffect(() => {
    const touch = () => {
      lastInteraction.current = Date.now();
      if (dormant) {
        setDormant(false);
        refresh();
      }
    };
    for (const ev of ['pointerdown', 'keydown', 'focus'] as const) {
      window.addEventListener(ev, touch);
    }
    return () => {
      for (const ev of ['pointerdown', 'keydown', 'focus'] as const) {
        window.removeEventListener(ev, touch);
      }
    };
  }, [dormant, refresh]);

  useEffect(() => {
    // Refresh on becoming visible, not just on a timer.
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const age = Date.now() - (lastFetchAt ?? 0);
      // Spec 13.3: a window open for twelve hours will cross a date
      // boundary and survive a laptop sleep. On wake, check elapsed wall
      // clock time and hard refresh rather than trusting incremental state.
      if (age > 30_000) refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refresh, lastFetchAt]);

  useEffect(() => {
    // Fetch every 60s when visible, every 5 min when hidden, and stop
    // entirely after an hour with no interaction. The only thing worth
    // guarding is a window left open over a weekend. Spec 13.5.
    const tick = setInterval(() => {
      const idleFor = Date.now() - lastInteraction.current;
      if (idleFor > 60 * 60_000) {
        setDormant(true);
        return;
      }
      const hidden = document.visibilityState !== 'visible';
      const age = Date.now() - (lastFetchAt ?? 0);
      if (age > (hidden ? 5 * 60_000 : 60_000)) refresh();
    }, 15_000);
    return () => clearInterval(tick);
  }, [refresh, lastFetchAt]);

  return { dormant };
}
